<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  slaEvents: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const TYPES = [
  {
    key: 'frt',
    i18nKey: 'SLA_REPORTS.MISS_TYPES.FRT',
    colorClass: 'bg-red-500',
  },
  {
    key: 'nrt',
    i18nKey: 'SLA_REPORTS.MISS_TYPES.NRT',
    colorClass: 'bg-amber-400',
  },
  {
    key: 'rt',
    i18nKey: 'SLA_REPORTS.MISS_TYPES.RT',
    colorClass: 'bg-orange-500',
  },
];

const typeCounts = computed(() =>
  props.slaEvents.reduce(
    (acc, event) => {
      const eventType = event?.event_type;
      if (eventType && acc[eventType] !== undefined) {
        acc[eventType] += 1;
      }
      return acc;
    },
    { frt: 0, nrt: 0, rt: 0 }
  )
);

const totalMisses = computed(() =>
  Object.values(typeCounts.value).reduce((sum, count) => sum + count, 0)
);

const getTypeLabel = key => {
  if (key === 'frt') return t('SLA_REPORTS.MISS_TYPES.FRT');
  if (key === 'nrt') return t('SLA_REPORTS.MISS_TYPES.NRT');
  return t('SLA_REPORTS.MISS_TYPES.RT');
};

const breakdownItems = computed(() => {
  const total = totalMisses.value;
  return TYPES.map(type => {
    const count = typeCounts.value[type.key];
    const pct = total ? Math.round((count / total) * 100) : 0;
    return {
      ...type,
      label: getTypeLabel(type.key),
      count,
      pct,
    };
  }).filter(item => item.count > 0);
});

const formatPct = value => `${value}%`;
</script>

<template>
  <div
    class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-4"
  >
    <span class="text-sm font-medium text-n-slate-11">
      {{ $t('SLA_REPORTS.BREAKDOWN.TITLE') }}
    </span>

    <div v-if="isLoading" class="mt-4 space-y-3">
      <div
        v-for="loader in 3"
        :key="loader"
        class="h-8 w-full rounded-lg bg-n-slate-3 animate-pulse"
      />
    </div>

    <div v-else class="mt-4 space-y-3">
      <div
        v-for="item in breakdownItems"
        :key="item.key"
        class="flex items-center gap-3"
      >
        <div class="flex items-center gap-2 min-w-[14rem]">
          <span class="size-2.5 rounded-full" :class="item.colorClass" />
          <span class="text-sm text-n-slate-11">
            {{ item.label }}
          </span>
        </div>
        <div class="w-full h-2 rounded-full overflow-hidden bg-n-alpha-2">
          <div
            class="h-full rounded-full transition-all duration-300"
            :class="item.colorClass"
            :style="{ width: `${item.pct}%` }"
          />
        </div>
        <span
          class="text-xs font-medium text-n-slate-12 w-14 text-right shrink-0"
        >
          {{ item.count }}
          <span class="text-n-slate-11 font-normal">{{
            formatPct(item.pct)
          }}</span>
        </span>
      </div>
    </div>
  </div>
</template>
